import React, { useState } from 'react';
import { FadeIn, CTAButton, Section, SectionTitle, Accent, pad2 } from './Shared';
import { LocalCurrencyNote } from './LocalCurrencyNote';
import { useCountdown } from '../hooks/useCountdown';
import { markCheckoutStarted } from '../hooks/useExitIntent';
import { PRODUCTS } from './MetaPixel';
import { tomos, sessionFeatures, featureChips } from '../data/tomos';
import { bonuses } from '../data/bonuses';
import { Check, AlertTriangle, Plus, PrinterIcon, ArrowRightIcon, Lock, ZapIcon, ShieldCheck, BookOpenIcon, GiftIcon, CheckCircle2 } from 'lucide-react';
export const ProductTomos = () => {
  const [openTomo, setOpenTomo] = useState<number | null>(0);
  return <Section className="bg-slate-950" data-id="element-27" data-mp="2cpq91w5or8u9">
      <FadeIn data-id="element-28" data-mp="2coni5o8d56ec">
        <SectionTitle sub="Un tomo cada dos meses. Abres, eliges la sesión y dictas." data-id="element-29" data-mp="2cof1rmd17iye">
          6 Tomos <Accent data-id="element-30" data-mp="30dzrqp7lrgh6">completos</Accent>
        </SectionTitle>
      </FadeIn>

      {/* Year timeline */}
      <FadeIn className="mx-auto mb-8 max-w-4xl" data-id="element-31" data-mp="357u93lzqjjd3">
        <div className="grid grid-cols-6 gap-1.5 md:gap-2" aria-label="Tu año, tomo por tomo" data-id="element-32" data-mp="2hl1vmg6xdlwx">
          {tomos.map(t => <div key={t.number} data-id="element-33" data-mp="2cn9ssr4qsfdy">
              <div className={`h-1.5 rounded-full ${t.bar}`} data-id="element-34" data-mp="2co2zxt7m56wm" />
              <p className={`mt-2 font-display text-[11px] font-extrabold uppercase md:text-sm ${t.text}`} data-id="element-35" data-mp="2rbo47qg85c64">
                Tomo {t.number}
              </p>
              <p className="hidden text-xs text-slate-500 md:block" data-id="element-36" data-mp="2miuzv3oo45uk">{t.months}</p>
            </div>)}
        </div>
      </FadeIn>

      <FadeIn className="mb-12 md:mb-16" data-id="element-37" data-mp="2war837kgxcci">
        <ul className="mx-auto flex max-w-3xl flex-wrap justify-center gap-1.5 md:gap-2" data-id="element-38" data-mp="2w9y0k4doqitb">
          {featureChips.map(chip => <li key={chip} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-200 md:text-sm" data-id="element-39" data-mp="3a864wxd1wmei">
              <Check size={12} className="text-sky-400" strokeWidth={3} aria-hidden="true" data-id="element-40" data-mp="1fybwh4hcg9eo" />
              {chip}
            </li>)}
        </ul>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400 md:text-sm" data-id="element-41" data-mp="3azxh0avp835i">
          <PrinterIcon size={14} aria-hidden="true" data-id="element-42" data-mp="3fziiot296plx" />
          <span data-id="element-43" data-mp="3g0bqlxcy7l5n">
            <span className="font-semibold text-slate-200" data-id="element-44" data-mp="3k0c51i01rwcp">Listo para imprimir</span> o usar
            directo desde tu celular/tablet.
          </span>
        </p>
      </FadeIn>

      <h3 className="mb-5 text-center text-xs font-semibold uppercase tracking-widest text-slate-400" data-id="element-45" data-mp="3owsviiupymsy">
        Lo que recibes
      </h3>
      <div className="mb-16 grid grid-cols-1 items-start gap-3 md:mb-24 md:grid-cols-2 md:gap-4 lg:grid-cols-3" data-id="element-46" data-mp="2r5m9alri6ww">
        {tomos.map((t, i) => {
        const isOpen = openTomo === i;
        return <FadeIn key={t.number} delay={Math.min(i * 0.04, 0.15)} data-id="element-47" data-mp="bksfou2hyy2x" data-mpi={i}>
              <button type="button" onClick={() => setOpenTomo(isOpen ? null : i)} aria-expanded={isOpen} className={`group w-full rounded-2xl border border-t-[3px] bg-white/[0.03] p-5 text-left transition-[border-color,background-color] duration-200 ease-out hover:bg-white/[0.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 md:p-6 ${t.border} ${isOpen ? 'border-x-white/20 border-b-white/20' : 'border-x-white/10 border-b-white/10'}`} data-id="element-48" data-mp="bm3jgtvy05u1">
                <div className="flex items-start justify-between gap-3" data-id="element-49" data-mp="hn42aptfxkkm">
                  <div className="min-w-0" data-id="element-50" data-mp="hoymu30svao6">
                    <p className={`font-display text-xs font-extrabold uppercase tracking-wide ${t.text}`} data-id="element-51" data-mp="1ebtmwnwgcraa">
                      Tomo {t.number} · {t.months}
                    </p>
                    <h4 className="mt-1.5 font-display text-lg font-extrabold leading-tight text-white md:text-xl" data-id="element-52" data-mp="14mjwdch9bgoa">
                      {t.title}
                    </h4>
                    <p className="mt-1 text-sm text-slate-400" data-id="element-53" data-mp="1x3cmekr9pn7k">{t.sessions}</p>
                  </div>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-white transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-54" data-mp="1scnxnd6z34c4">
                    <Plus size={16} data-id="element-55" data-mp="1hu8ybn138eme" />
                  </span>
                </div>
                <div className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-56" data-mp="13ty8mnbil62t">
                  <div className="overflow-hidden" data-id="element-57" data-mp="17xmft4w8hznj">
                    <p className="pt-4 text-[15px] leading-relaxed text-slate-300" data-id="element-58" data-mp="1vkum9q5efer5">{t.desc}</p>
                    <ul className="mt-3 space-y-2" data-id="element-59" data-mp="20kgxxv5kwmxu">
                      {t.details.map(d => <li key={d} className="flex items-start gap-2 text-sm leading-snug text-slate-300" data-id="element-60" data-mp="1mssojzmpsckk">
                          <Check size={14} className={`mt-0.5 shrink-0 ${t.text}`} strokeWidth={3} aria-hidden="true" data-id="element-61" data-mp="1qu5pbvay9pkm" />
                          {d}
                        </li>)}
                    </ul>
                  </div>
                </div>
              </button>
            </FadeIn>;
      })}
      </div>

      <FadeIn data-id="element-62" data-mp="7v7szap6wrur">
        <div className="grid gap-8 rounded-3xl border border-white/10 bg-slate-900/60 p-6 md:grid-cols-[0.9fr_1.1fr] md:gap-12 md:p-12" data-id="element-63" data-mp="7uoclyf6ubiu">
          <div className="flex flex-col" data-id="element-64" data-mp="2uk713ftj17e">
            <h3 className="font-display text-3xl font-black uppercase italic leading-[1.05] tracking-tight text-white md:text-5xl" data-id="element-65" data-mp="hm2hklpws3jd">
              Cada sesión <Accent data-id="element-66" data-mp="cn813qfeyr2f">incluye</Accent>
            </h3>
            
          </div>
          <ul className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2" data-id="element-67" data-mp="3l3189enuuq6t">
            {sessionFeatures.map(f => <li key={f.title} className="flex items-start gap-3" data-id="element-68" data-mp="3g4ge9wz752qt">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15" data-id="element-69" data-mp="3v0ywi25psmzf">
                  <Check size={12} className="text-emerald-400" strokeWidth={3} aria-hidden="true" data-id="element-70" data-mp="3q0jid8dlzbge" />
                </span>
                <div data-id="element-71" data-mp="1a8dk41x2seit">
                  <p className="font-semibold leading-snug text-white" data-id="element-72" data-mp="15b3w8qpc4ax3">{f.title}</p>
                  <p className="text-sm leading-normal text-slate-400" data-id="element-73" data-mp="15d7wmyo0yrsm">{f.desc}</p>
                </div>
              </li>)}
          </ul>
        <p className="mt-5 text-lg font-semibold leading-snug text-sky-300 md:mt-auto md:text-xl" data-id="element-74" data-mp="1hcvfnh3kleuy">
              Abres el TOMO, eliges la sesión y dictas. Así de simple. 365 clases de
              alto rendimiento. Listas desde hoy.
            </p></div>
      </FadeIn>
    </Section>;
};
export const Bonuses = () => <Section className="bg-slate-900/40" data-id="element-75" data-mp="tolodznl7ywf">
    <FadeIn data-id="element-76" data-mp="tpnacz6565sx">
      <SectionTitle sub="Materiales exclusivos — sin costo extra" data-id="element-77" data-mp="tlzfnwoyndcm">
        + 5 Bonos <Accent data-id="element-78" data-mp="yn6o7xyxggkc">estratégicos</Accent>
      </SectionTitle>
    </FadeIn>

    <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:mb-10 md:gap-4 lg:grid-cols-5" data-id="element-79" data-mp="13kyrtp8xpr4d">
      {bonuses.map((bono, i) => <FadeIn key={bono.num} delay={Math.min(i * 0.04, 0.15)} className={i === bonuses.length - 1 ? 'sm:col-span-2 lg:col-span-1' : ''} data-id="element-80" data-mp="18e9nqvc7knhi" data-mpi={i}>
          <article className="flex h-full gap-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-3 sm:flex-col sm:gap-0 sm:p-0" data-id="element-81" data-mp="fwfg0zqoew7j">
            <img src={bono.image} alt={`Portada del bono ${bono.num}: ${bono.title}`} loading="lazy" decoding="async" width={400} height={533} className="aspect-[3/4] w-24 shrink-0 self-start rounded-xl object-cover sm:w-full sm:rounded-none" data-id="element-82" data-mp="2t5dvk88ls7gz" />
            <div className="flex min-w-0 flex-1 flex-col sm:p-4" data-id="element-83" data-mp="2f6wdor5dpp6z">
              <p className="font-display text-xs font-extrabold uppercase tracking-wide text-sky-400" data-id="element-84" data-mp="2f6o1l1h2ynwu">
                Bono {bono.num}
              </p>
              <h3 className="mt-1 font-display text-base font-extrabold leading-tight text-white" data-id="element-85" data-mp="25gky27cc1rk9">
                {bono.title}
              </h3>
              <p className="mt-1.5 text-sm leading-snug text-slate-400" data-id="element-86" data-mp="21fr9dbr2g8qb">{bono.desc}</p>
              <div className="mt-auto flex items-center justify-between gap-2 pt-3" data-id="element-87" data-mp="1whzjen9zewor">
                <span className="text-sm text-slate-500 line-through" data-id="element-88" data-mp="1rg0eva2i9tq8">{bono.value}</span>
                <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-400" data-id="element-89" data-mp="1mj9zyq2ft46e">
                  Gratis
                </span>
              </div>
            </div>
          </article>
        </FadeIn>)}
    </div>

    <FadeIn className="mx-auto flex max-w-3xl flex-col items-center justify-between gap-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] px-6 py-5 text-center md:flex-row md:text-left" data-id="element-90" data-mp="31e8jdu5ew51b">
      <div className="flex items-center gap-3" data-id="element-91" data-mp="2rop2uv568n9m">
        <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 md:flex" data-id="element-92" data-mp="2rmvv1citnx01">
          <GiftIcon size={22} className="text-emerald-400" aria-hidden="true" data-id="element-93" data-mp="2wh9sdkh1oe7c" />
        </span>
        <div data-id="element-94" data-mp="2iplfheyz1lpb">
          <p className="text-sm text-slate-300" data-id="element-95" data-mp="2ilxp2ootpfet">
            Valor total de los 5 Bonos: <span className="text-slate-500 line-through" data-id="element-96" data-mp="2nn3iypu7z8px">USD $40</span>
          </p>
          <p className="text-xs text-slate-500" data-id="element-97" data-mp="28qw1hxa74lec">Incluidos en tu MEGAPACK, sin costo extra.</p>
        </div>
      </div>
      <p className="font-display text-3xl font-black uppercase italic leading-none tracking-tight text-emerald-400 md:text-4xl" data-id="element-98" data-mp="2dnw8caevds6i">
        Gratis — con tu MEGAPACK
      </p>
    </FadeIn>
  </Section>;
export const UrgencyCounter = () => {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown();
  const units = [{
    value: hours,
    label: 'hs'
  }, {
    value: minutes,
    label: 'min'
  }, {
    value: seconds,
    label: 'seg'
  }];
  return <Section className="bg-slate-950 !py-14 md:!py-20" data-id="element-99" data-mp="2d0yffo8t1t43">
      <FadeIn className="mx-auto max-w-xl text-center" data-id="element-100" data-mp="2czdl9zqekc6r">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-400" data-id="element-101" data-mp="28zd80if1i71a">
          Precio de lanzamiento — cierra en
        </p>
        <div className="mt-4 flex items-start justify-center gap-2 md:gap-3" role="timer" aria-live="off" data-id="element-102" data-mp="2mtdzqs7kqts5">
          {units.map((unit, i) => <React.Fragment key={unit.label} data-id="element-103" data-mp="2wp7kuzzczvlj" data-mpi={i}>
              {i > 0 && <span className="pt-3 font-display text-4xl font-black text-slate-600 md:pt-4 md:text-5xl" aria-hidden="true" data-id="element-104" data-mp="2wnmqpbgyieo7">
                  :
                </span>}
              <div className="flex w-20 flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] py-3 md:w-24 md:py-4" data-id="element-105" data-mp="2qyalrs17726w">
                <span className="font-display text-4xl font-black tabular-nums leading-none tracking-tight text-white md:text-5xl" data-id="element-106" data-mp="2m4ze5ookahdx">
                  {pad2(unit.value)}
                </span>
                <span className="mt-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-500" data-id="element-107" data-mp="2d418z32e03tr">
                  {unit.label}
                </span>
              </div>
            </React.Fragment>)}
        </div>

        <p className="mt-6 flex items-center justify-center gap-2 text-base font-semibold text-amber-300 md:text-lg" data-id="element-108" data-mp="30vgdn2hmyx86">
          <AlertTriangle size={18} aria-hidden="true" data-id="element-109" data-mp="2vxevcjs1b1xr" />
          Después sube el precio. No vuelve.
        </p>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left" data-id="element-110" data-mp="1phkxbqezwifd">
          <div className="mb-3 flex justify-between gap-3 text-sm text-slate-300" data-id="element-111" data-mp="1kgf3tqdigr4s">
            <span className="flex items-center gap-2" data-id="element-112" data-mp="3w5btwjoeua5n">
              <span className="h-2 w-2 animate-pulse rounded-full bg-sky-400" aria-hidden="true" data-id="element-113" data-mp="3w2pe5wrd8occ" />
              Cupos al precio de lanzamiento
            </span>
            <span className="font-semibold text-sky-400" data-id="element-114" data-mp="8z1cqnp9qcsr">Quedan pocos</span>
          </div>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={71} aria-valuemin={0} aria-valuemax={100} aria-label="Cupos ya tomados" data-id="element-115" data-mp="dr2q6cy6a9kd">
            <div className="h-full w-[71%] rounded-full bg-blue-500" data-id="element-116" data-mp="no7cccs0q516" />
          </div>
          <p className="text-right text-xs text-slate-500" data-id="element-117" data-mp="socy5cblwxbc">71% de los cupos ya tomados</p>
        </div>
      </FadeIn>
    </Section>;
};

/** Shared offer card used by the pricing section and the final CTA. */
export const OfferCard = ({
  id,
  footnote,
  checklist
}: {
  id?: string;
  footnote: string;
  checklist?: string[];
}) => {
  const includes = [{
    icon: BookOpenIcon,
    label: '6 Tomos Completos',
    detail: '365 sesiones listas para dictar',
    price: 'USD $39'
  }, {
    icon: GiftIcon,
    label: '5 Bonos Exclusivos',
    detail: 'Adaptaciones, warm-ups, cierres y scripts',
    price: 'USD $40'
  }];
  return <div id={id} className="relative rounded-3xl border border-sky-400/40 bg-slate-900 p-5 shadow-2xl shadow-blue-950/50 md:p-8" data-id="element-118" data-mp="eo136it8bx9e">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-600 px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white" data-id="element-119" data-mp="anqz5bozzgx6">
        Más elegido
      </div>

      <p className="mt-2 text-center font-display text-xl font-black uppercase italic tracking-tight text-white md:text-2xl" data-id="element-120" data-mp="jkhz3z6o7fx1">
        MEGAPACK Funcional <Accent data-id="element-121" data-mp="td7183gbzhfp">365™</Accent>
      </p>
      <p className="mt-1 text-center text-xs font-semibold uppercase tracking-widest text-slate-500" data-id="element-122" data-mp="te8m0zn5fiam">
        {checklist ? 'Lo que incluye' : 'Lo que incluye tu MEGAPACK'}
      </p>

      {checklist ? <ul className="mt-5 space-y-2.5 border-y border-white/10 py-4" data-id="element-123" data-mp="12ar2hce71dqb">
          {checklist.map(item => <li key={item} className="flex items-start gap-2.5 text-sm text-slate-200 md:text-base" data-id="element-124" data-mp="1lzf7ournqzwt">
              <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-emerald-400" aria-hidden="true" data-id="element-125" data-mp="1qvmcgk8bi5d6" />
              {item}
            </li>)}
        </ul> : <ul className="mt-5 divide-y divide-white/10 border-y border-white/10" data-id="element-126" data-mp="1gyzpnpwfl48i">
          {includes.map(({
        icon: Icon,
        label,
        detail,
        price
      }) => <li key={label} className="flex items-center justify-between gap-3 py-3" data-id="element-127" data-mp="17yum5qucypzm">
              <div className="flex min-w-0 items-start gap-3" data-id="element-128" data-mp="180nurboj7ka9">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5" data-id="element-129" data-mp="tb9m65ss56vx">
                  <Icon size={16} className="text-sky-400" aria-hidden="true" data-id="element-130" data-mp="y9kq5zwe8n68" />
                </span>
                <div className="min-w-0" data-id="element-131" data-mp="ka1xmfg2fkf1">
                  <p className="text-sm font-semibold leading-snug text-white md:text-base" data-id="element-132" data-mp="p83ectq0r7nc">{label}</p>
                  <p className="text-xs leading-snug text-slate-400 md:text-sm" data-id="element-133" data-mp="h7du6k5pafrg">{detail}</p>
                </div>
              </div>
              <span className="shrink-0 text-sm text-slate-500 line-through" data-id="element-134" data-mp="c9vl8troo0jg">{price}</span>
            </li>)}
        </ul>}

      <div className="mt-6 text-center" data-id="element-135" data-mp="7g15g1f785pn">
        <p className="text-sm text-slate-400" data-id="element-136" data-mp="3giiz17qbmkp">
          Valor original <span className="text-slate-500 line-through" data-id="element-137" data-mp="3g0ifse1am6z">USD $79</span> · Acceso completo hoy por
        </p>
        <p className="mt-2 font-display font-black italic leading-none tracking-tight text-white" data-id="element-138" data-mp="3ukh0ifrc24wv">
          <span className="align-top text-3xl md:text-4xl" data-id="element-139" data-mp="3ppjulhpcobo7">$</span>
          <span className="text-7xl md:text-8xl" data-id="element-140" data-mp="3kp5ldwc0ivol">17</span>
          <span className="ml-1 text-base font-bold not-italic text-slate-400" data-id="element-141" data-mp="3gn27l11vff05">USD</span>
        </p>
        <p className="mt-2 text-xs text-slate-400 md:text-sm" data-id="element-142" data-mp="1iwr32w5k3p7n">{footnote}</p>
      </div>

      <LocalCurrencyNote className="mt-5" data-id="element-143" data-mp="1evfm1f0r0cb2" />

      <div className="mt-5" data-id="element-144" data-mp="1ewqqzi5xjq3r">
        <CTAButton href={PRODUCTS.megapack.checkoutUrl} fullWidth trackingProduct="megapack" onClick={markCheckoutStarted} data-id="element-145" data-mp="1etb9y9sp46zp">
          Quiero el MEGAPACK completo
          <ArrowRightIcon size={18} className="transition-transform duration-200 group-hover/cta:translate-x-0.5" aria-hidden="true" data-id="element-146" data-mp="31r3r5kqa5zbw" />
        </CTAButton>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-500" data-id="element-147" data-mp="3akfpo877cnst">
          <span className="flex items-center gap-1" data-id="element-148" data-mp="2dug6zkxc88wi"><Lock size={12} aria-hidden="true" data-id="element-149" data-mp="2dvhreg08u7qw" /> Pago seguro</span>
          <span className="flex items-center gap-1" data-id="element-150" data-mp="2isrh7wrjolf9"><ZapIcon size={12} aria-hidden="true" data-id="element-151" data-mp="2itt1mrugak9n" /> Acceso en minutos</span>
          <span className="flex items-center gap-1" data-id="element-152" data-mp="2mttggdlgoxh8"><ShieldCheck size={12} aria-hidden="true" data-id="element-153" data-mp="2mwz8o1pe0fh6" /> Garantía 7 días</span>
        </div>
      </div>
    </div>;
};
export const Pricing = () => <Section className="bg-slate-900/40" id="pricing" data-id="element-154" data-mp="255gyh8t4kc2">
    <div className="mx-auto max-w-lg" data-id="element-155" data-mp="24drlo5sziu8">
      <FadeIn className="mb-10 text-center" data-id="element-156" data-mp="737a5uzy6019">
        <p className="text-sm text-slate-400 md:text-base" data-id="element-157" data-mp="725m8pxu1j24">
          Estás a un paso de resolver todas tus clases...
        </p>
        <h2 className="mx-auto mt-3 font-display text-4xl font-black uppercase italic leading-[1.05] tracking-tight text-white md:text-6xl" data-id="element-158" data-mp="238pqf1r58wae">
          Oferta especial de <Accent data-id="element-159" data-mp="2d4j2kk0yanrn">lanzamiento</Accent>
        </h2>
        <p className="mt-3 text-sm text-slate-400" data-id="element-160" data-mp="2867sc86qub8w">
          Tomos, bonos y 365 sesiones listas en un solo acceso.
        </p>
      </FadeIn>
      <FadeIn delay={0.05} data-id="element-161" data-mp="1kh5fddn216kq">
        <OfferCard footnote="Pago único · Sin suscripciones · Acceso inmediato" data-id="element-162" data-mp="1keauxm41jlrb" />
      </FadeIn>
    </div>
  </Section>;